import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Admin.scss';

import rightArrow1 from '../../assets/rightLast.svg';
import rightArrow2 from '../../assets/right.svg';
import leftArrow1 from '../../assets/leftLast.svg';
import leftArrow2 from '../../assets/left.svg';
import iconDelete from '../../assets/icon_delete.svg';
import AppSpinner from '../../components/AppSpinner';

interface CommentData {
  _id: string;
  content: string;
  createdAt: string;
  deleted: boolean;
  user: {
    _id: string;
    displayName: string;
  } | null;
  post: {
    _id: string;
    title: string;
  } | null;
}

const AdminCommentListPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [comments, setComments] = useState<CommentData[]>([]);
  const [filteredComments, setFilteredComments] = useState<CommentData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [searchType, setSearchType] = useState<string>('all');
  const [keyword, setKeyword] = useState<string>('');

  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;

  const [selectedCommentIds, setSelectedCommentIds] = useState<
    { commentId: string; postId: string }[]
  >([]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchComments = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/community/comments/all', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setComments(response.data);
        setFilteredComments(response.data);
      } catch (err) {
        setError('댓글 목록을 불러오는 데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [isAuthenticated]);

  const handleSearch = () => {
    let result = [...comments];

    if (keyword) {
      const lowerKeyword = keyword.toLowerCase();
      result = result.filter(comment => {
        const contentMatch = comment.content?.toLowerCase().includes(lowerKeyword);
        const authorMatch = comment.user?.displayName?.toLowerCase().includes(lowerKeyword);
        const postTitleMatch = comment.post?.title?.toLowerCase().includes(lowerKeyword);

        if (searchType === 'content') return contentMatch;
        if (searchType === 'author') return authorMatch;
        if (searchType === 'postTitle') return postTitleMatch;
        return contentMatch || authorMatch || postTitleMatch;
      });
    }

    setFilteredComments(result);
    setCurrentPage(1);
    setSelectedCommentIds([]);
  };

  const handleReset = () => {
    setSearchType('all');
    setKeyword('');
    setFilteredComments(comments);
    setCurrentPage(1);
    setSelectedCommentIds([]);
  };

  const totalPages = Math.ceil(filteredComments.length / itemsPerPage);
  const paginatedComments = filteredComments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = paginatedComments.map(c => ({ commentId: c._id, postId: c.post?._id || '' }));
      setSelectedCommentIds(prev => {
        const newSelection = [...prev];
        allIds.forEach(item => {
          if (!newSelection.some(s => s.commentId === item.commentId)) {
            newSelection.push(item);
          }
        });
        return newSelection;
      });
    } else {
      const pageIds = paginatedComments.map(c => c._id);
      setSelectedCommentIds(prev => prev.filter(item => !pageIds.includes(item.commentId)));
    }
  };

  const handleSelectComment = (commentId: string, postId: string) => {
    setSelectedCommentIds(prev =>
      prev.some(item => item.commentId === commentId)
        ? prev.filter(item => item.commentId !== commentId)
        : [...prev, { commentId, postId }]
    );
  };

  const isAllSelected =
    paginatedComments.length > 0 &&
    paginatedComments.every(c => selectedCommentIds.some(s => s.commentId === c._id));

  const handleDeleteSelected = async () => {
    if (selectedCommentIds.length === 0) {
      alert('삭제할 댓글을 선택해주세요.');
      return;
    }

    if (!window.confirm(`선택한 ${selectedCommentIds.length}개의 댓글을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await Promise.all(
        selectedCommentIds.map(item =>
          axios.delete(`/api/community/${item.postId}/comment/${item.commentId}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );
      alert('삭제되었습니다.');

      const remainingComments = comments.filter(
        c => !selectedCommentIds.some(s => s.commentId === c._id)
      );
      setComments(remainingComments);
      setFilteredComments(prev =>
        prev.filter(c => !selectedCommentIds.some(s => s.commentId === c._id))
      );
      setSelectedCommentIds([]);
    } catch (err) {
      alert('삭제 중 오류가 발생했습니다.');
      console.error(err);
    }
  };

  if (loading) return <AppSpinner fullscreen tip="Loading..." />;
  if (error) return <div className="admin-page">에러: {error}</div>;

  return (
    <div className="admin-page">
      <h1>댓글 관리</h1>

      <div className="search-filter-container">
        <div className="filter-group">
          <label>검색</label>
          <div className="search-input-group">
            <select value={searchType} onChange={e => setSearchType(e.target.value)}>
              <option value="all">전체</option>
              <option value="content">내용</option>
              <option value="author">작성자</option>
              <option value="postTitle">게시글 제목</option>
            </select>
            <input
              type="text"
              placeholder="검색어를 입력하세요"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </div>
        </div>

        <div className="button-group">
          <button className="btn-search" onClick={handleSearch}>
            검색
          </button>
          <button className="btn-reset" onClick={handleReset}>
            초기화
          </button>
        </div>
      </div>
      <div className="list-info">
        총 댓글 <span className="count">{filteredComments.length}</span> | 페이지{' '}
        <span className="page">
          {currentPage} / {totalPages || 1}
        </span>
      </div>

      <div className="table-scroll">
        <table className="table comment-list-table">
          <colgroup>
            <col width="50px" />
            <col width="5%" />
            <col width="25%" />
            <col width="40%" />
            <col width="10%" />
            <col width="15%" />
          </colgroup>
          <thead>
            <tr>
              <th>
                <label className="custom-checkbox">
                  <input type="checkbox" checked={isAllSelected} onChange={handleSelectAll} />
                  <span className="checkmark"></span>
                </label>
              </th>
              <th>No</th>
              <th>게시글 제목</th>
              <th>댓글 내용</th>
              <th>작성자</th>
              <th>작성일</th>
            </tr>
          </thead>
          <tbody>
            {paginatedComments.length > 0 ? (
              paginatedComments.map((comment, index) => (
                <tr key={comment._id}>
                  <td>
                    <label className="custom-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedCommentIds.some(s => s.commentId === comment._id)}
                        onChange={() => handleSelectComment(comment._id, comment.post?._id || '')}
                      />
                      <span className="checkmark"></span>
                    </label>
                  </td>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>
                    <p className="ellipsis-1">
                    {comment.post ? (
                      <Link
                        to={`/admin/community/${comment.post._id}`}
                        style={{ color: '#333', textDecoration: 'none', fontWeight: 600 }}
                      >
                        {comment.post.title.length > 15
                          ? comment.post.title.substring(0, 15) + '...'
                          : comment.post.title}
                      </Link>
                    ) : (
                      <span style={{ color: '#999' }}>(삭제된 게시글)</span>
                    )}
                    </p>
                  </td>
                  <td style={{ textAlign: 'left', paddingLeft: '10px', wordBreak: 'break-all' }}>
                    <div
                      dangerouslySetInnerHTML={{ __html: comment.content }}
                      style={{
                        maxHeight: '60px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    />
                  </td>
                  <td>{comment.user ? comment.user.displayName : '(알 수 없음)'}</td>
                  <td>
                    {new Date(comment.createdAt).toLocaleDateString()}{' '}
                    {new Date(comment.createdAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="no-data">
                  데이터가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <div className="pagination">
          <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
            <img src={leftArrow1} alt="화살표" />
          </button>
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            <img src={leftArrow2} alt="화살표" />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              className={currentPage === page ? 'active' : ''}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            <img src={rightArrow1} alt="화살표" />
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            <img src={rightArrow2} alt="화살표" />
          </button>
        </div>

        <div className="bottom-actions">
          <button className="btn-delete-selection" onClick={handleDeleteSelected}>
            <img src={iconDelete} alt="삭제" />
            삭제
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminCommentListPage;
